import { Alert } from '@dbrain/ui';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, NavLink } from 'react-router';
import { useMe } from '../../api/hooks.js';
import { PageHeader } from '../../components/PageHeader.js';
import { useCapabilities } from '../../experience.js';
import { adminSections, SECTIONS, type AdminSection } from './sections.js';

export { adminSections, isDomainAdmin, isOrganizationAdmin, type AdminSection } from './sections.js';

/** /admin: the first tab this person may open. */
export function AdminHome() {
  const me = useMe().data;
  const capabilities = useCapabilities();
  if (!me) return null;
  const first = adminSections(me, capabilities)[0];
  return <Navigate to={first ? `/admin/${first}` : '/admin/access'} replace />;
}

export function AdminLayout({ section, title, intro, actions, children }: { section: AdminSection; title: string; intro?: ReactNode; actions?: ReactNode; children: ReactNode }) {
  const { t } = useTranslation();
  const me = useMe().data;
  const capabilities = useCapabilities();
  if (!me) return null;
  const sections = adminSections(me, capabilities);
  const allowed = sections.includes(section);
  return (
    <div className="page page--wide admin-page">
      <PageHeader eyebrow={t('shell.management')} title={title} intro={allowed ? intro : undefined} actions={allowed ? actions : undefined} />
      {sections.length > 0 ? (
        <nav className="admin-tabs" aria-label={t('admin.nav')}>
          {SECTIONS.filter((s) => sections.includes(s.id)).map(({ id, icon: Icon }) => (
            <NavLink key={id} to={`/admin/${id}`} className="admin-tabs__item" data-testid={`admin-nav-${id}`}>
              <Icon size={16} />
              {t(`admin.sections.${id}`)}
            </NavLink>
          ))}
        </nav>
      ) : null}
      <div className="admin-content">{allowed ? children : <Alert tone="warning" title={t(SECTIONS.find((s) => s.id === section)?.superAdmin ? 'admin.onlySuperAdmins' : 'admin.onlyAdmins')} data-testid="admin-only" />}</div>
    </div>
  );
}

/** The sub-tabs of a page ("Üyeler 300", "Domain'ler 6", "Sistem hesapları 1"). */
export function SubTabs({ items, label }: { items: { to: string; label: string; count?: number | undefined; testId: string; end?: boolean }[]; label: string }) {
  const { i18n } = useTranslation();
  return (
    <nav className="sub-tabs" aria-label={label}>
      {items.map((item) => (
        <NavLink key={item.to} to={item.to} end={item.end ?? false} className="sub-tabs__item" data-testid={item.testId}>
          {item.label}
          {item.count !== undefined ? <span className="sub-tabs__count">{item.count.toLocaleString(i18n.language)}</span> : null}
        </NavLink>
      ))}
    </nav>
  );
}

/** Sub-tabs that filter one list or switch a page's panel in place ("Tümü 1.312", "Modeller 9"). */
export function PillTabs<T extends string>({
  items,
  value,
  onChange,
  label,
}: {
  items: { id: T; label: string; count?: number | undefined; testId: string }[];
  value: T;
  onChange: (id: T) => void;
  label: string;
}) {
  const { i18n } = useTranslation();
  return (
    <div className="sub-tabs" role="group" aria-label={label}>
      {items.map((item) => (
        <button
          key={item.id}
          type="button"
          className={`sub-tabs__item${item.id === value ? ' active' : ''}`}
          aria-pressed={item.id === value}
          onClick={() => onChange(item.id)}
          data-testid={item.testId}
        >
          {item.label}
          {item.count !== undefined ? <span className="sub-tabs__count">{item.count.toLocaleString(i18n.language)}</span> : null}
        </button>
      ))}
    </div>
  );
}
